import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {ModalAlert} from '@/components/alerts';
import {Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import EmailFetchAccountConnectionCheck from './EmailFetchAccountConnectionCheck';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import TableComponent from '@/components/table/Table';
import EmailFetchAccountsSettingsModal from './EmailFetchAccountsSettingsModal';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface EmailFetchAccount {
    id: number;
    email: string;
    credentials: {
        email: string;
    };
    updatedOn: string;
    lastUpdated: string;
    createdOn: string;
    connection_status: string;
}

interface UserData {
    permissions?: string[];
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const EmailFetchAccountsListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const setSearchQueryState = async (setSearchQuery: (value: string) => void, value: string, setPage: (page: number) => void): Promise<void> => {
        setPage(1);
        setSearchQuery(value);
    };

    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedEmailFetchAccountId, setEmailFetchAccountId] = useState<number>(0);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{ id: number, email: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const userCanCreateEmailFetchAccounts = hasPermission('fs.email_sync.account.create', userData?.permissions, true);
    const userCanChangeEmailFetchAccounts = hasPermission('fs.email_sync.fetch_task.update', userData?.permissions, true);
    const userCanDeleteEmailFetchAccounts = hasPermission('fs.email_sync.account.delete', userData?.permissions, true);

    const {
        data: emailFetchAccounts,
        loading: loading,
        currentPage,
        setCurrentPage,
        pages,
    }: PaginatedDataResult<EmailFetchAccount> = usePaginatedData(window.URLS.emailFetchAccountsResource, {
        storeResolver: 'emailFetchAccounts',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage || 1;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const userCanAccessEmailFetchAccountsResource = hasPermission('fs.email_sync.account.list', userData?.permissions, true);

    const deleteAction = useStoreActions((actions: any) => actions.emailFetchAccounts.deleteOne);

    const openSettingsModal = (emailFetchAccountId: number): void => {
        setEmailFetchAccountId(emailFetchAccountId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setEmailFetchAccountId(0);
    };

    const openDeleteConfirm = (id: number, email: string): void => {
        setItemToDelete({id, email});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.emailFetchAccountsResource}/${itemToDelete.id}`;
            await axios.delete(url);
            deleteAction(itemToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, email: string): void => {
        openDeleteConfirm(id, email);
    };

    const columns = [
        {
            header: i18n.t('th_email.text'),
            accessorKey: 'email',
        },
        {
            header: i18n.t('th_credentials.text'),
            accessorKey: 'credentials.email',
        },
        {
            accessorKey: 'updatedOn',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: EmailFetchAccount) => {
                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.lastUpdated, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_connection.text'),
            accessorKey: 'connection_status',
            cell: (cell: any) => (
                <EmailFetchAccountConnectionCheck
                    id={cell.row.original.id}
                    key={JSON.stringify(cell.row)}
                />
            ),
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                return (
                    <>
                        <div className="d-inline-block">
                            <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                                <Dropdown className="d-inline-block">
                                    <Dropdown.Toggle
                                        as={Button}
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-dots-horizontal"
                                        className="dropdown-toggle-no-caret"
                                        data-cy="email-fetch-accounts-settings-toggle"
                                        id="settings-dropdown-1"
                                    />

                                    <Dropdown.Menu align="end">
                                        {
                                            userCanChangeEmailFetchAccounts ? (
                                                <Dropdown.Item
                                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                                    data-cy="email-fetch-accounts-settings-open-button"
                                                >
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="email-fetch-accounts-list-btn-1">
                                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                        {
                                            userCanDeleteEmailFetchAccounts ? (
                                                <Dropdown.Item
                                                    onClick={() => deleteOne(cell.row.original.id, cell.row.original.email)}
                                                >
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="email-fetch-accounts-list-btn">
                                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Tooltip>
                        </div>
                    </>
                );
            },
        },
    ];

    if (!userCanAccessEmailFetchAccountsResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_accounts_mailboxes.text')} />

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    return (
        <>

            <HelpTextBlock helpText={i18n.t('ht_accounts_mailboxes.text')} />

            <Row>
                <Col lg={3}>
                    <Form className="mb-2 d-flex align-items-center search-box" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                    }}>
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    <div>
                        {
                            userCanCreateEmailFetchAccounts ? (
                                <Button
                                    variant="info"
                                    size="sm"
                                    className="mb-2"
                                    icon="mdi-plus"
                                    onClick={() => setSettingsModalOpen(true)}
                                    data-cy="new-email-fetch-account-btn"
                                >
                                    {i18n.t('jt_add_new_account.text')}
                                </Button>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="mb-2"
                                            icon="mdi-plus"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="email-fetch-accounts-list-plus-btn">
                                            {i18n.t('jt_add_new_account.text')}
                                        </Button>
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                </Col>
            </Row>

            {
                loading ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <div className="card">
                        <div className="card-body">
                                <TableComponent data={emailFetchAccounts} columns={columns} />

                                <PaginationComponent
                                    pages={pages}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                        </div>


                        {
                            settingsModalOpen ? (
                                <EmailFetchAccountsSettingsModal
                                    modalOpen={settingsModalOpen}
                                    closeModal={closeSettingsModal}
                                    activeEmailFetchAccount={selectedEmailFetchAccountId}
                                />
                            ) : null
                        }

                        <ModalAlert
                            isOpen={deleteConfirmOpen}
                            onClose={closeDeleteConfirm}
                            variant="error"
                            title={i18n.t('jt_confirm_deletion.text')}
                            message={itemToDelete ? i18n.t('es_are_you_sure_delete_email_fetch.text', {email: itemToDelete.email}) : ''}
                            actions={[
                                {
                                    label: i18n.t('jt_delete.text'),
                                    onClick: confirmDelete,
                                    variant: 'danger',
                                    loading: isDeleting
                                }
                            ]}
                            dismissible={!isDeleting}
                        />
                    </div>
                )
            }

        </>
    )
});

export default EmailFetchAccountsListView; 